import React, { useState, useMemo, useEffect } from 'react';
import {
  BrowserRouter as Router,
  Route,
  Switch,
  Redirect,
} from 'react-router-dom';
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { createGlobalStyle } from 'styled-components';
import { Toaster } from 'react-hot-toast';
import GlobalStyles from '../globalStyles';
import AccessLevels from '../util/AccessLevels';
// Auth Layout
import AuthLayout from './layouts/AuthLayout';
import EnterPaymentPage from './EnterPaymentPage';
import Login from './Login';
import Register from './Register';
import ForgotPassword from './ForgotPassword';
import ResetPassword from './ResetPassword';
import Onboarding from './Onboarding';
import NewAccountFlow from './new-account/NewAccountFlow';
// Admin Layout
import AdminLayout from './layouts/AdminLayout';
import Dashboard from './dashboard/Dashboard';
import RedeemCoupon from './redeem-coupons/RedeemCoupon';
import Customers from './customers/Customers';
import SuperadminCustomers from './superadmin-customers/SuperadminCustomers';
import SuperadminCampaigns from './superadmin-campaigns/SuperadminCampaigns';
import Settings from './Settings';
import SuperAdmin from './superadmin/SuperAdmin';
import { UserContext } from '../context/UserContext';
import { AppContext } from '../context/AppContext';
import * as Intercom from '../util/Intercom';
import Campaigns from './campaign/Campaigns';
import CreateCampaign from './campaign/CreateCampaign';
import EditCampaign from './campaign/EditCampaign';
import { useDesktopMedia } from '../util/MediaQuery';
import CustomerTransactions from './customers/CustomerTransactions';

// should do this the dotenv way to distingush between prod and staging/dev
// load stripe with public publishable keys
const stripePromise = window.location.origin === 'https://app.checkthisout.io'
  ? loadStripe('pk_live_Auo8VY0Eedg461Z0CuOCVWtv00wWBdtgS1')
  : loadStripe('pk_test_cwPSCdi3vdFFoBYh6Z7cWbXb00TG1Zlt5S');

const fonts = [
  {
    cssSrc:
      'https://fonts.googleapis.com/css?family=Poppins:300,400,500,600,700',
    family: 'Poppins',
  },
];

export default function App() {
  const [app, setApp] = useState({
    onboarding: {
      checklistSidebarOpen: false,
      focusedChecklistStepId: null,
      continueWidgetAfterStepId: null,
      tourTargetMissStepId: null,
      tourSuppressForStepId: null,
      tourRetryKey: 0,
      /** Persists across AdminLayout remounts when the route changes (separate <Route> per path). */
      tourScreenIndex: 0,
      /** Prevents repeat auto-navigation on every AdminLayout remount for the same org (session). */
      autoNavigatedForOrganizationId: null,
      /** When true (Continue widget open), skip auto-focus/tour until user picks a checklist step. */
      checklistDeferTourUntilStepClick: false,
      /** Incremented when a checklist tour final step succeeds; onboarding modal watches this (cache-safe). */
      onboardingCompleteModalBump: 0,
    },
  });
  const [user, setUser] = useState(null);
  const userValue = useMemo(() => ({ user, setUser }), [user, setUser]);
  const appValue = useMemo(() => ({ app, setApp }), [app, setApp]);

  const isDesktop = useDesktopMedia();

  useEffect(() => {
    // prevent android keyboard shrinking viewport when
    // open and affecting percentage based div heights for whole app
    // https://stackoverflow.com/questions/32963400/android-keyboard-shrinking-the-viewport-and-elements-using-unit-vh-in-css
    // const viewport = document.querySelector('meta[name=viewport]');
    // if (viewport) {
    //   viewport.setAttribute(
    //     'content',
    //     `height=${window.innerHeight}, width=${window.innerWidth}, initial-scale=1.0`,
    //   );
    // }
    Intercom.connect();
  }, []);

  const HideToolTip = createGlobalStyle`
     .__react_component_tooltip{
      display: none;
    }
  
  `;

  return (
    <Router basename="/admin">
      <GlobalStyles />
      {!isDesktop && <HideToolTip />}
      <Elements stripe={stripePromise} options={{ fonts }}>
        <Switch>
          {/* Non-Authed routes */}
          <Route exact path="/">
            <Redirect to="/login" />
          </Route>
          <AuthLayout path="/login" component={Login} />
          <AuthLayout path="/register" component={Register} />
          <AuthLayout path="/forgotPassword" component={ForgotPassword} />
          <AuthLayout path="/resetPassword" component={ResetPassword} />
          <AuthLayout path="/onboarding" component={Onboarding} />
          <AuthLayout path="/new-account/step/:stepNum" component={NewAccountFlow} centered={false} />
          <AuthLayout path="/enterPayment" component={EnterPaymentPage} />
          {/* Auth required routes */}
          <AppContext.Provider value={appValue}>
            <UserContext.Provider value={userValue}>
              <AdminLayout
                path="/dashboard"
                component={Dashboard}
                rolesAllowed={[AccessLevels.SuperAdmin, AccessLevels.Admin]}
              />
              <AdminLayout
                path="/customers"
                component={Customers}
                rolesAllowed={[AccessLevels.SuperAdmin, AccessLevels.Admin]}
              />
              <AdminLayout
                path="/customers/:phoneNumber"
                component={CustomerTransactions}
                rolesAllowed={[AccessLevels.SuperAdmin, AccessLevels.Admin]}
              />
              <AdminLayout
                path="/redeemCoupon"
                component={RedeemCoupon}
                rolesAllowed={[
                  AccessLevels.SuperAdmin,
                  AccessLevels.Admin,
                  AccessLevels.Server,
                ]}
              />
              <AdminLayout
                path="/settings"
                component={Settings}
                rolesAllowed={[AccessLevels.SuperAdmin, AccessLevels.Admin]}
              />
              <AdminLayout
                path="/campaigns"
                component={Campaigns}
                rolesAllowed={[AccessLevels.SuperAdmin, AccessLevels.Admin]}
              />
              <AdminLayout
                path="/campaigns/new"
                component={CreateCampaign}
                rolesAllowed={[AccessLevels.SuperAdmin, AccessLevels.Admin]}
              />
              <AdminLayout
                path="/campaigns/id=:campaignId"
                component={EditCampaign}
                rolesAllowed={[AccessLevels.SuperAdmin, AccessLevels.Admin]}
              />
              <AdminLayout
                path="/superAdminCustomers"
                component={SuperadminCustomers}
                rolesAllowed={[
                  AccessLevels.SuperAdmin,
                  // AccessLevels.Admin,
                ]}
              />
              <AdminLayout
                path="/superAdminCampaigns"
                component={SuperadminCampaigns}
                rolesAllowed={[
                  AccessLevels.SuperAdmin,
                ]}
              />

              <AdminLayout
                path="/superAdmin"
                component={SuperAdmin}
                rolesAllowed={[
                  AccessLevels.SuperAdmin,
                  // AccessLevels.Admin,
                ]}
              />
            </UserContext.Provider>
          </AppContext.Provider>
          {/* 404 */}
          <Redirect to="/" />
        </Switch>
      </Elements>
      <Toaster
        toastOptions={{
          duration: 3000,
          style: {
            background: '#363636',
            color: '#fff',
            fontSize: '14px',
          },
        }}
        position="top-right"
      />
    </Router>
  );
}
